import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Calendar, Clock, User, Phone, Mail, Car, CheckCircle, XCircle, Filter, RefreshCw, LogOut, Star, Plus, Eye, EyeOff, Trash2, Edit2, Save, X, Settings, MessageSquare, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { useToast } from '../components/Toast';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  confirmed: 'bg-green-100 text-green-800 border-green-300',
  completed: 'bg-blue-100 text-blue-800 border-blue-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};
const STATUS_LABELS = { pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled' };

const format12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

const AdminPage = () => {
  const toast = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [activeTab, setActiveTab] = useState('reservations');

  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [filterStatus, setFilterStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [confirmingId, setConfirmingId] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [actionLoading, setActionLoading] = useState('');

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [showAddReview, setShowAddReview] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, date: '', service: 'Automotive Tinting', review: '', location: '', source: 'google', visible: true });

  // Settings state
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});

  // ─── Auth ───
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    try {
      await axios.post('/api/admin/login', { password });
      setIsAuthenticated(true);
    } catch { setLoginError('Invalid password'); setPassword(''); }
  };

  // ─── Reservations ───
  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterStatus ? { status: filterStatus } : {};
      const res = await axios.get('/api/reservations', { params });
      setReservations(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterStatus]);

  const handleConfirmClick = async (res) => {
    setConfirmingId(res.id); setSelectedSlot('');
    try {
      const resp = await axios.get(`/api/reservations/available-slots?date_str=${res.preferred_date}`);
      setAvailableSlots(resp.data.slots);
    } catch { setAvailableSlots([]); }
  };

  const handleConfirmSubmit = async (resId) => {
    if (!selectedSlot) return;
    setActionLoading(resId);
    try {
      await axios.patch(`/api/reservations/${resId}/confirm`, { confirmed_time: selectedSlot });
      setConfirmingId(null); setSelectedSlot(''); fetchReservations();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to confirm'); }
    finally { setActionLoading(''); }
  };

  const handleStatusChange = async (resId, action) => {
    setActionLoading(resId);
    try { await axios.patch(`/api/reservations/${resId}/${action}`); fetchReservations(); toast.success(`Reservation ${action}led`); }
    catch (err) { toast.error(err.response?.data?.detail || `Failed to ${action}`); }
    finally { setActionLoading(''); }
  };

  // ─── Reviews ───
  const fetchReviews = useCallback(async () => {
    setReviewsLoading(true);
    try {
      const res = await axios.get('/api/reviews/all');
      setReviews(res.data);
    } catch (err) { console.error(err); }
    finally { setReviewsLoading(false); }
  }, []);

  const handleSaveReview = async () => {
    try {
      if (editingReview) {
        await axios.patch(`/api/reviews/${editingReview}`, reviewForm);
      } else {
        await axios.post('/api/reviews', reviewForm);
      }
      setShowAddReview(false); setEditingReview(null);
      setReviewForm({ name: '', rating: 5, date: '', service: 'Automotive Tinting', review: '', location: '', source: 'google', visible: true });
      fetchReviews();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save review'); }
  };

  const handleEditReview = (rev) => {
    setEditingReview(rev.id);
    setReviewForm({ name: rev.name, rating: rev.rating, date: rev.date, service: rev.service, review: rev.review, location: rev.location || '', source: rev.source || 'google', visible: rev.visible });
    setShowAddReview(true);
  };

  const handleToggleVisibility = async (rev) => {
    try {
      await axios.patch(`/api/reviews/${rev.id}`, { visible: !rev.visible });
      fetchReviews();
    } catch (err) { toast.error('Failed to update review'); }
  };

  const handleDeleteReview = async (rev) => {
    if (!window.confirm(`Delete review from ${rev.name}?`)) return;
    try { await axios.delete(`/api/reviews/${rev.id}`); fetchReviews(); }
    catch (err) { toast.error('Failed to delete review'); }
  };

  // ─── Settings ───
  const fetchSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const res = await axios.get('/api/settings/business');
      setSettingsForm({
        name: res.data.name || '',
        phone: res.data.phone || '',
        email: res.data.email || '',
        street: res.data.address?.street || '',
        suite: res.data.address?.suite || '',
        city: res.data.address?.city || '',
        state: res.data.address?.state || '',
        zip: res.data.address?.zip || '',
        weekdays: res.data.hours?.weekdays || '',
        saturday: res.data.hours?.saturday || '',
        sunday: res.data.hours?.sunday || '',
        serviceAreas: (res.data.serviceAreas || []).join(', '),
      });
    } catch (err) { console.error(err); }
    finally { setSettingsLoading(false); }
  }, []);

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await axios.put('/api/settings/business', {
        name: settingsForm.name,
        phone: settingsForm.phone,
        email: settingsForm.email,
        address: { street: settingsForm.street, suite: settingsForm.suite, city: settingsForm.city, state: settingsForm.state, zip: settingsForm.zip },
        hours: { weekdays: settingsForm.weekdays, saturday: settingsForm.saturday, sunday: settingsForm.sunday },
        serviceAreas: settingsForm.serviceAreas.split(',').map(s => s.trim()).filter(Boolean),
      });
      fetchSettings();
      toast.success('Settings saved!');
    } catch (err) { toast.error('Failed to save settings'); }
    finally { setSettingsSaving(false); }
  };

  // ─── Load data per tab ───
  useEffect(() => {
    if (!isAuthenticated) return;
    if (activeTab === 'reservations') fetchReservations();
    else if (activeTab === 'reviews') fetchReviews();
    else if (activeTab === 'settings') fetchSettings();
  }, [isAuthenticated, activeTab, filterStatus, fetchReservations, fetchReviews, fetchSettings]);

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3"><Lock className="h-7 w-7 text-red-600" /></div>
            <CardTitle>Admin Login</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="password" placeholder="Enter admin password" value={password} onChange={(e) => { setPassword(e.target.value); setLoginError(''); }} autoFocus />
              {loginError && <p className="text-sm text-red-600">{loginError}</p>}
              <Button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white">Log In</Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  const counts = {
    pending: reservations.filter(r => r.status === 'pending').length,
    confirmed: reservations.filter(r => r.status === 'confirmed').length,
    completed: reservations.filter(r => r.status === 'completed').length,
    cancelled: reservations.filter(r => r.status === 'cancelled').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <Button variant="outline" size="sm" onClick={() => setIsAuthenticated(false)}>
            <LogOut className="h-4 w-4 mr-1" /> Logout
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-gray-200 rounded-lg p-1">
          {[
            { key: 'reservations', label: 'Reservations', icon: Calendar },
            { key: 'reviews', label: 'Reviews', icon: MessageSquare },
            { key: 'settings', label: 'Settings', icon: Settings },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex-1 flex items-center justify-center space-x-1.5 py-2 px-3 rounded-md text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* ═══ RESERVATIONS TAB ═══ */}
        {activeTab === 'reservations' && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              {Object.entries(STATUS_LABELS).map(([key, label]) => (
                <button key={key} onClick={() => setFilterStatus(filterStatus === key ? '' : key)}
                  className={`rounded-lg border-2 p-3 text-center transition-all ${filterStatus === key ? 'ring-2 ring-red-400' : ''} ${STATUS_COLORS[key]}`}>
                  <p className="text-2xl font-bold">{counts[key] || 0}</p>
                  <p className="text-xs font-medium">{label}</p>
                </button>
              ))}
            </div>

            {filterStatus && (
              <div className="flex items-center space-x-2 mb-4">
                <Filter className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Showing: {STATUS_LABELS[filterStatus]}</span>
                <button onClick={() => setFilterStatus('')} className="text-sm text-red-600 hover:underline">Clear</button>
              </div>
            )}

            <div className="flex justify-end mb-4">
              <Button variant="outline" size="sm" onClick={fetchReservations}>
                <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : reservations.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No reservations found.</div>
            ) : (
              <div className="space-y-3">
                {reservations.map(res => (
                  <Card key={res.id} className="overflow-hidden">
                    <div className="flex flex-col md:flex-row">
                      <div className={`md:w-2 w-full h-1 md:h-auto ${res.status === 'pending' ? 'bg-yellow-400' : res.status === 'confirmed' ? 'bg-green-500' : res.status === 'completed' ? 'bg-blue-500' : 'bg-red-400'}`} />
                      <CardContent className="flex-1 py-4">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                          <div className="space-y-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-lg">{res.name}</h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${STATUS_COLORS[res.status]}`}>{STATUS_LABELS[res.status]}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                              <span className="flex items-center space-x-1"><Phone className="h-3.5 w-3.5" /><a href={`tel:${res.phone}`} className="hover:text-red-600">{res.phone}</a></span>
                              <span className="flex items-center space-x-1"><Mail className="h-3.5 w-3.5" /><span>{res.email}</span></span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
                              <span className="flex items-center space-x-1"><Calendar className="h-3.5 w-3.5 text-gray-400" /><span>{res.preferred_date}</span></span>
                              {res.confirmed_time && <span className="flex items-center space-x-1 text-green-700 font-medium"><Clock className="h-3.5 w-3.5" /><span>{format12h(res.confirmed_time)}</span></span>}
                              <span className="capitalize">{res.service_type.replace('_', ' ')}</span>
                              {res.vehicle_info && <span className="flex items-center space-x-1"><Car className="h-3.5 w-3.5 text-gray-400" /><span>{res.vehicle_info}</span></span>}
                            </div>
                            {res.notes && <p className="text-sm text-gray-500 italic">"{res.notes}"</p>}
                          </div>
                          <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
                            {res.status === 'pending' && (
                              <>
                                <Button size="sm" onClick={() => handleConfirmClick(res)} className="bg-green-600 hover:bg-green-700 text-white text-xs" disabled={actionLoading === res.id}>
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Confirm
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleStatusChange(res.id, 'cancel')} className="text-red-600 border-red-300 hover:bg-red-50 text-xs" disabled={actionLoading === res.id}>
                                  <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                                </Button>
                              </>
                            )}
                            {res.status === 'confirmed' && (
                              <>
                                <Button size="sm" onClick={() => handleStatusChange(res.id, 'complete')} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" disabled={actionLoading === res.id}>
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" /> Complete
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleStatusChange(res.id, 'cancel')} className="text-red-600 border-red-300 hover:bg-red-50 text-xs" disabled={actionLoading === res.id}>
                                  <XCircle className="h-3.5 w-3.5 mr-1" /> Cancel
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {confirmingId === res.id && (
                          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                            <p className="font-medium text-green-800 mb-2">Select a time slot for {res.preferred_date}:</p>
                            {availableSlots.length === 0 ? (
                              <p className="text-sm text-gray-600">No available slots.</p>
                            ) : (
                              <>
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {availableSlots.map(slot => (
                                    <button key={slot.time} onClick={() => setSelectedSlot(slot.time)}
                                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${selectedSlot === slot.time ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'}`}>
                                      {slot.display}
                                    </button>
                                  ))}
                                </div>
                                <div className="flex space-x-2">
                                  <Button size="sm" onClick={() => handleConfirmSubmit(res.id)} disabled={!selectedSlot || actionLoading === res.id} className="bg-green-600 hover:bg-green-700 text-white">
                                    {actionLoading === res.id ? 'Confirming...' : `Confirm at ${selectedSlot ? format12h(selectedSlot) : '...'}`}
                                  </Button>
                                  <Button size="sm" variant="outline" onClick={() => { setConfirmingId(null); setSelectedSlot(''); }}>Cancel</Button>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══ REVIEWS TAB ═══ */}
        {activeTab === 'reviews' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-gray-500">{reviews.length} review{reviews.length !== 1 ? 's' : ''}</p>
              <div className="flex space-x-2">
                <Button variant="outline" size="sm" onClick={fetchReviews}>
                  <RefreshCw className={`h-4 w-4 mr-1 ${reviewsLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
                <Button size="sm" onClick={() => { setEditingReview(null); setReviewForm({ name: '', rating: 5, date: new Date().toISOString().split('T')[0], service: 'Automotive Tinting', review: '', location: '', source: 'google', visible: true }); setShowAddReview(true); }} className="bg-red-600 hover:bg-red-700 text-white">
                  <Plus className="h-4 w-4 mr-1" /> Add Review
                </Button>
              </div>
            </div>

            {showAddReview && (
              <Card className="mb-6 border-2 border-red-200">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{editingReview ? 'Edit Review' : 'Add New Review'}</CardTitle>
                    <button onClick={() => { setShowAddReview(false); setEditingReview(null); }}><X className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div><Label>Customer Name</Label><Input value={reviewForm.name} onChange={e => setReviewForm(f => ({ ...f, name: e.target.value }))} placeholder="John Smith" /></div>
                    <div><Label>Location</Label><Input value={reviewForm.location} onChange={e => setReviewForm(f => ({ ...f, location: e.target.value }))} placeholder="Missouri City, TX" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>Rating</Label>
                      <div className="flex space-x-1 mt-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setReviewForm(f => ({ ...f, rating: n }))}>
                            <Star className={`h-6 w-6 ${n <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                          </button>
                        ))}
                      </div>
                    </div>
                    <div><Label>Date</Label><Input type="date" value={reviewForm.date} onChange={e => setReviewForm(f => ({ ...f, date: e.target.value }))} /></div>
                    <div>
                      <Label>Service</Label>
                      <select value={reviewForm.service} onChange={e => setReviewForm(f => ({ ...f, service: e.target.value }))} className="w-full mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm">
                        <option>Automotive Tinting</option>
                        <option>Residential Tinting</option>
                        <option>Commercial Tinting</option>
                      </select>
                    </div>
                  </div>
                  <div><Label>Review Text</Label><Textarea value={reviewForm.review} onChange={e => setReviewForm(f => ({ ...f, review: e.target.value }))} rows={3} placeholder="Customer's review..." /></div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Source</Label>
                      <select value={reviewForm.source} onChange={e => setReviewForm(f => ({ ...f, source: e.target.value }))} className="w-full mt-1 rounded-md border border-gray-300 px-3 py-2 text-sm">
                        <option value="google">Google</option>
                        <option value="yelp">Yelp</option>
                        <option value="facebook">Facebook</option>
                        <option value="manual">Manual</option>
                      </select>
                    </div>
                    <div className="flex items-end">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input type="checkbox" checked={reviewForm.visible} onChange={e => setReviewForm(f => ({ ...f, visible: e.target.checked }))} className="rounded border-gray-300" />
                        <span className="text-sm">Visible on website</span>
                      </label>
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2 pt-2">
                    <Button variant="outline" onClick={() => { setShowAddReview(false); setEditingReview(null); }}>Cancel</Button>
                    <Button onClick={handleSaveReview} className="bg-red-600 hover:bg-red-700 text-white">
                      <Save className="h-4 w-4 mr-1" /> {editingReview ? 'Update' : 'Add'} Review
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {reviewsLoading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : reviews.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No reviews yet.</div>
            ) : (
              <div className="space-y-3">
                {reviews.map(rev => (
                  <Card key={rev.id} className={`${!rev.visible ? 'opacity-60' : ''}`}>
                    <CardContent className="py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center space-x-2 flex-wrap">
                            <h3 className="font-semibold">{rev.name}</h3>
                            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className={`h-3.5 w-3.5 ${i < rev.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />)}</div>
                            {!rev.visible && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">Hidden</span>}
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{rev.source}</span>
                          </div>
                          <div className="flex flex-wrap gap-x-3 text-xs text-gray-500">
                            <span>{rev.date}</span>
                            {rev.location && <span className="flex items-center space-x-0.5"><MapPin className="h-3 w-3" /><span>{rev.location}</span></span>}
                            <span>{rev.service}</span>
                          </div>
                          <p className="text-sm text-gray-700 italic">"{rev.review}"</p>
                        </div>
                        <div className="flex space-x-1">
                          <button onClick={() => handleToggleVisibility(rev)} className="p-1.5 rounded hover:bg-gray-100" title={rev.visible ? 'Hide' : 'Show'}>
                            {rev.visible ? <Eye className="h-4 w-4 text-green-600" /> : <EyeOff className="h-4 w-4 text-gray-400" />}
                          </button>
                          <button onClick={() => handleEditReview(rev)} className="p-1.5 rounded hover:bg-gray-100" title="Edit">
                            <Edit2 className="h-4 w-4 text-blue-600" />
                          </button>
                          <button onClick={() => handleDeleteReview(rev)} className="p-1.5 rounded hover:bg-gray-100" title="Delete">
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}

        {/* ═══ SETTINGS TAB ═══ */}
        {activeTab === 'settings' && (
          <>
            {settingsLoading ? (
              <div className="text-center py-12 text-gray-500">Loading...</div>
            ) : (
              <div className="space-y-6">
                <Card>
                  <CardHeader><CardTitle className="text-lg">Business Information</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><Label>Business Name</Label><Input value={settingsForm.name || ''} onChange={e => setSettingsForm(f => ({ ...f, name: e.target.value }))} /></div>
                      <div><Label>Phone</Label><Input value={settingsForm.phone || ''} onChange={e => setSettingsForm(f => ({ ...f, phone: e.target.value }))} /></div>
                    </div>
                    <div><Label>Email</Label><Input value={settingsForm.email || ''} onChange={e => setSettingsForm(f => ({ ...f, email: e.target.value }))} /></div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-lg">Address</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div><Label>Street</Label><Input value={settingsForm.street || ''} onChange={e => setSettingsForm(f => ({ ...f, street: e.target.value }))} /></div>
                      <div><Label>Suite</Label><Input value={settingsForm.suite || ''} onChange={e => setSettingsForm(f => ({ ...f, suite: e.target.value }))} /></div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <div><Label>City</Label><Input value={settingsForm.city || ''} onChange={e => setSettingsForm(f => ({ ...f, city: e.target.value }))} /></div>
                      <div><Label>State</Label><Input value={settingsForm.state || ''} onChange={e => setSettingsForm(f => ({ ...f, state: e.target.value }))} /></div>
                      <div><Label>ZIP</Label><Input value={settingsForm.zip || ''} onChange={e => setSettingsForm(f => ({ ...f, zip: e.target.value }))} /></div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-lg">Business Hours</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div><Label>Weekdays</Label><Input value={settingsForm.weekdays || ''} onChange={e => setSettingsForm(f => ({ ...f, weekdays: e.target.value }))} placeholder="Mon - Fri: 9:00 AM - 6:00 PM" /></div>
                    <div><Label>Saturday</Label><Input value={settingsForm.saturday || ''} onChange={e => setSettingsForm(f => ({ ...f, saturday: e.target.value }))} placeholder="Sat: 9:00 AM - 5:00 PM" /></div>
                    <div><Label>Sunday</Label><Input value={settingsForm.sunday || ''} onChange={e => setSettingsForm(f => ({ ...f, sunday: e.target.value }))} placeholder="Sun: Closed" /></div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader><CardTitle className="text-lg">Service Areas</CardTitle></CardHeader>
                  <CardContent>
                    <Label>Areas (comma-separated)</Label>
                    <Input value={settingsForm.serviceAreas || ''} onChange={e => setSettingsForm(f => ({ ...f, serviceAreas: e.target.value }))} placeholder="Houston, Missouri City, Sugar Land..." />
                    <p className="text-xs text-gray-500 mt-1">Separate each area with a comma</p>
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button onClick={handleSaveSettings} disabled={settingsSaving} className="bg-red-600 hover:bg-red-700 text-white px-8">
                    <Save className="h-4 w-4 mr-2" />
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
